import { useCallback, useId } from 'react';
import { useLens } from '@hookform/lenses';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { match } from 'ts-pattern';
import { z } from 'zod/v4';
import { APIError, ApiErrors } from '@interxfi/api-client/api-error';
import { useLogin } from '@interxfi/api-client/userauth/login';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { ButtonLink } from '@interxfi/design-system/components/button/button-link';
import { useButtonState } from '@interxfi/design-system/components/button/button-state';
import { TextButton } from '@interxfi/design-system/components/link/text-link/text-button';
import { VR } from '@interxfi/design-system/components/separator/vr/vr';
import { Typography } from '@interxfi/design-system/components/typography';
import { decodeFromBase64 } from '@interxfi/utilities/encoding/base64';
import { AppSingleDigitInputWithLens } from '../../../../components/form/app-single-digit-input';
import { AppTextInputWithLens } from '../../../../components/form/app-text-input';
import { AuthLayout } from '../../../../features/auth/layout';
import { usePendingCredentials } from '../../../../features/auth/login/pending-credentials-context';
import { useGetDefaultAuthenticatedRoute } from '../../../../util/auth';
import { useErrorHandler } from '../../../../util/error-handler';
import { DEFAULT_AUTHENTICATED_ROUTE } from '../../../../util/routing/defaults';

const loginSearchSchema = z.object({
  redirectTo: z.string().optional(),
});

export const Route = createFileRoute('/_auth/login/_login/mfa')({
  component: LoginMfaEntry,
  validateSearch: loginSearchSchema,
});

export const mfaFormSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('otp'),
    code: z.string(), // The OTP is only submitted when all six digits are populated
  }),
  z.object({
    kind: z.literal('recoveryCode'),
    code: z.string().nonempty({ error: 'Recovery Code is required' }),
  }),
]);

export type MFAFormValues = z.infer<typeof mfaFormSchema>;

const defaultValuesByType: Record<MFAFormValues['kind'], MFAFormValues> = {
  otp: { kind: 'otp', code: '' },
  recoveryCode: { kind: 'recoveryCode', code: '' },
} as const;

function loginErrorMessageGetter(error: Error | null): string | undefined {
  if (!error) {
    return undefined;
  }

  if (error instanceof APIError) {
    if (error.message === ApiErrors.MissingOTP) {
      return undefined;
    }

    if ([403, 401].includes(error.status)) {
      return 'Oops, the credentials you provided were invalid. Please try again.';
    }

    if (error.status === 503) {
      return 'Service temporarily unavailable, please try your request again later.';
    }
  }

  return error.message;
}

function LoginMfaEntry() {
  const getRedirectTo = useGetDefaultAuthenticatedRoute();
  const idPrefix = useId();
  const navigate = useNavigate();
  const { redirectTo: redirectToAfterAuthenticated } = Route.useSearch();
  const { email: pendingEmail, password: pendingPassword, clear: clearPendingCredentials } = usePendingCredentials();
  const { mutateAsync, data: loginResponse, isPending, error } = useLogin();
  useErrorHandler(error, loginErrorMessageGetter);
  const [buttonState, buttonRef] = useButtonState(isPending, loginErrorMessageGetter(error));

  const handleLogin = useCallback(
    async (otp: string) => {
      try {
        const response = await mutateAsync({
          credentials: {
            basicAuth: {
              email: pendingEmail,
              password: pendingPassword,
              otp,
            },
          },
        });

        if (!response?.newRecoveryCode) {
          await navigate({
            to: redirectToAfterAuthenticated ? decodeFromBase64(redirectToAfterAuthenticated) : await getRedirectTo(),
          });
        }
      } catch (err) {
        if (err instanceof APIError) {
          if (err.message === ApiErrors.MissingOTP) {
            clearPendingCredentials();
            await navigate({ to: '/login', search: redirectToAfterAuthenticated ? { redirectTo: redirectToAfterAuthenticated } : undefined });
          }
        }
      }
    },
    [clearPendingCredentials, mutateAsync, navigate, pendingEmail, pendingPassword, redirectToAfterAuthenticated, getRedirectTo],
  );

  const handleFillOtp = useCallback(
    async (otp: string) => {
      try {
        await handleLogin(otp);
      } catch {}
    },
    [handleLogin],
  );

  const form = useForm<MFAFormValues>({
    defaultValues: defaultValuesByType.otp,
    mode: 'all',
    resolver: zodResolver(mfaFormSchema),
  });

  const {
    control,
    formState: { isValid },
    handleSubmit,
    setValue,
  } = form;

  const lens = useLens({ control });

  const kind = useWatch({ control, name: 'kind' });

  async function onSubmit(value: MFAFormValues) {
    try {
      await handleLogin(
        match(value)
          .with({ kind: 'otp' }, ({ code }) => code)
          .with({ kind: 'recoveryCode' }, ({ code }) => code)
          .otherwise(() => ''),
      );
    } catch {
    } finally {
      setValue('code', '');
    }
  }

  if (!pendingEmail || !pendingPassword) {
    return <Navigate search={redirectToAfterAuthenticated ? { redirectTo: redirectToAfterAuthenticated } : undefined} to="/login" />;
  }

  return (
    <FormProvider {...form}>
      <AuthLayout>
        {loginResponse?.newRecoveryCode ? (
          <VerticalStack gap={7}>
            <Typography as="h1" lineHeight="1.5rem" size="1.5rem" variant="heading">
              Backup Code Used
            </Typography>

            <Typography lineHeight="1.5rem" size="1rem">
              You used your backup code to sign in. We&apos;ve generated a new one for you. You can use this one-time backup code to access your
              account if you lose access to your other authentication factor. Write it down and keep it safe.
            </Typography>

            <Typography lineHeight="2rem" size="1.5rem">
              {loginResponse.newRecoveryCode}
            </Typography>

            <ButtonLink fullWidth LinkElement={Link} size="large" to={redirectToAfterAuthenticated || DEFAULT_AUTHENTICATED_ROUTE} variant="primary">
              OK, Got It
            </ButtonLink>
          </VerticalStack>
        ) : (
          match(kind)
            .with('otp', () => (
              <VerticalStack align="center" as="form" gap={7} onSubmit={handleSubmit(onSubmit)}>
                <Typography as="h1" lineHeight="1.5rem" size="1.5rem" variant="heading" weight="bold">
                  Enter Your Code
                </Typography>

                <VerticalStack gap={4}>
                  <Typography id={`${idPrefix}_otpDescription`} lineHeight="1.125rem" size="0.875rem">
                    Enter the code from your authenticator app.
                  </Typography>

                  <AppSingleDigitInputWithLens
                    aria-describedby={`${idPrefix}_otpDescription`}
                    autoFocus
                    disabled={isPending}
                    hideLabel
                    label="One-Time Password (OTP)"
                    length={6}
                    lens={lens.focus('code')}
                    onFill={handleFillOtp}
                    required
                  />
                </VerticalStack>

                <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
                  <TextButton
                    fullWidth
                    onClick={() => {
                      clearPendingCredentials();
                    }}
                    size="medium"
                    underline="hover"
                  >
                    Sign Out
                  </TextButton>

                  <VR style={{ flex: '0 0 auto', height: '16px' }} />

                  <TextButton
                    fullWidth
                    onClick={() => {
                      setValue('kind', 'recoveryCode');
                    }}
                    size="medium"
                    type="button"
                    underline="hover"
                  >
                    Use backup code
                  </TextButton>
                </HorizontalStack>
              </VerticalStack>
            ))
            .with('recoveryCode', () => (
              <VerticalStack align="center" as="form" gap={7} onSubmit={handleSubmit(onSubmit)}>
                <Typography as="h1" lineHeight="1.5rem" size="1.5rem" variant="heading" weight="bold">
                  Backup Code
                </Typography>

                <VerticalStack gap={4}>
                  <Typography id={`${idPrefix}_recoveryCodeDescription`} lineHeight="1.125rem" size="0.875rem">
                    Enter your single-use backup code.
                  </Typography>

                  <AppTextInputWithLens
                    aria-describedby={`${idPrefix}_recoveryCodeDescription`}
                    disabled={isPending}
                    label="Code"
                    lens={lens.focus('code')}
                    placeholder="ABCD-EFGH-IJKL-MNOP"
                    required
                  />

                  <Button disabled={!isValid || isPending} fullWidth ref={buttonRef} size="large" state={buttonState} type="submit">
                    Verify
                  </Button>
                </VerticalStack>

                <VerticalStack inlineAlign="center">
                  <TextButton
                    onClick={() => {
                      clearPendingCredentials();
                    }}
                    size="medium"
                    underline="hover"
                  >
                    Sign Out
                  </TextButton>
                </VerticalStack>
              </VerticalStack>
            ))
            .otherwise(() => null)
        )}
      </AuthLayout>
    </FormProvider>
  );
}
